import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Bell, X, CheckCircle, AlertTriangle, Info, Clock, FileText, Shield, Zap, ExternalLink, Download } from 'lucide-react';
import { notifications as notificationsApi, scans as scansApi, auth } from '../api/client';
import { useI18n } from '../i18n';

const NotificationCenter = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const buttonRef = useRef(null);

  useEffect(() => {
    // Charger les notifications depuis l'API backend
    fetchNotifications();

    // Rafraîchir toutes les 15 secondes
    const interval = setInterval(fetchNotifications, 15000);
    return () => clearInterval(interval);
  }, []);

  // Update dropdown position when opening
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right
      });
    }
  }, [isOpen]);

  const fetchNotifications = async () => {
    // Skip if not authenticated or token is expired
    if (!auth.isAuthenticated() || !auth.isTokenValidClientSide()) {
      return;
    }

    try {
      // Essayer d'abord la nouvelle API
      const data = await notificationsApi.list(20, 0, false);

      if (data && data.notifications) {
        // Transformer les notifications du backend
        const formattedNotifications = data.notifications.map(n => ({
          id: n.id,
          type: mapNotificationType(n.type, n.priority),
          title: n.title,
          message: n.message,
          timestamp: new Date(n.created_at),
          read: n.read,
          scanId: n.scan_id,
          reportFilename: n.report_filename,
          metadata: n.metadata
        }));

        setNotifications(formattedNotifications);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      // Silently handle 401 - user will be redirected to login by API client
      if (error.response?.status === 401) return;
      console.warn('Backend notifications not available, using scan-based notifications:', error);
      // Fallback: utiliser l'ancienne methode basee sur les scans
      await fetchScanBasedNotifications();
    }
  };

  // Fallback si l'API notifications n'est pas disponible
  const fetchScanBasedNotifications = async () => {
    try {
      const data = await scansApi.list(0, 100);
      const scansArray = data.scans || [];

      const newNotifications = [];

      scansArray.forEach(scan => {
        if (scan.status === 'completed' && scan.total_vulnerabilities > 0) {
          newNotifications.push({
            id: `scan-${scan.id}`,
            type: scan.critical_count > 0 ? 'critical' : 'success',
            title: scan.critical_count > 0
              ? t('notifications.criticalVulnsFound', { count: scan.critical_count })
              : t('notifications.scanCompleted'),
            message: t('notifications.hostsScanned', { count: scan.total_hosts_found, target: scan.target }),
            timestamp: new Date(scan.completed_at || scan.created_at),
            read: false,
            scanId: scan.id
          });
        }

        if (scan.status === 'running') {
          newNotifications.push({
            id: `running-${scan.id}`,
            type: 'info',
            title: t('notifications.scanRunning'),
            message: t('notifications.analyzing', { target: scan.target }),
            timestamp: new Date(scan.started_at || scan.created_at),
            read: true,
            scanId: scan.id
          });
        }

        if (scan.status === 'failed') {
          newNotifications.push({
            id: `failed-${scan.id}`,
            type: 'critical',
            title: t('notifications.scanFailed'),
            message: t('notifications.errorOn', { target: scan.target }),
            timestamp: new Date(scan.completed_at || scan.created_at),
            read: false,
            scanId: scan.id
          });
        }
      });

      newNotifications.sort((a, b) => b.timestamp - a.timestamp);
      setNotifications(newNotifications.slice(0, 10));
      setUnreadCount(newNotifications.filter(n => !n.read).length);
    } catch (error) {
      // Silently handle 401 - user will be redirected to login by API client
      if (error.response?.status !== 401) {
        console.error('Failed to fetch scan-based notifications:', error);
      }
    }
  };

  // Map backend notification type to UI type
  const mapNotificationType = (type, priority) => {
    // Types critiques
    if (type === 'vuln_critical' || type === 'scan_failed' || type === 'exploit_failed' || type === 'system_error') {
      return 'critical';
    }
    // Types succes
    if (type === 'scan_completed' || type === 'report_generated' || type === 'exploit_success' || type === 'session_opened') {
      return 'success';
    }
    // Warning
    if (type === 'vuln_high' || type === 'system_warning') {
      return 'warning';
    }
    // Info par defaut
    return 'info';
  };

  const markAsRead = async (id) => {
    try {
      // Si c'est un ID du backend (UUID), appeler l'API
      if (id && !id.startsWith('scan-') && !id.startsWith('running-') && !id.startsWith('failed-')) {
        await notificationsApi.markAsRead(id);
      }
      setNotifications(prev => prev.map(n =>
        n.id === id ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      // Silently handle 401 - user will be redirected to login by API client
      if (error.response?.status !== 401) {
        console.error('Failed to mark notification as read:', error);
      }
      // Marquer localement meme en cas d'erreur
      setNotifications(prev => prev.map(n =>
        n.id === id ? { ...n, read: true } : n
      ));
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationsApi.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      // Silently handle 401 - user will be redirected to login by API client
      if (error.response?.status !== 401) {
        console.error('Failed to mark all as read:', error);
      }
      // Marquer localement
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    }
  };

  const dismissNotification = async (id) => {
    try {
      if (id && !id.startsWith('scan-') && !id.startsWith('running-') && !id.startsWith('failed-')) {
        await notificationsApi.dismiss(id);
      }
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (error) {
      // Silently handle 401 - user will be redirected to login by API client
      if (error.response?.status !== 401) {
        console.error('Failed to dismiss notification:', error);
      }
      setNotifications(prev => prev.filter(n => n.id !== id));
    }
  };

  const clearAll = async () => {
    try {
      await notificationsApi.clearOld();
    } catch (error) {
      // Silently handle 401 - user will be redirected to login by API client
      if (error.response?.status !== 401) {
        console.error('Failed to clear old notifications:', error);
      }
    }
    setNotifications([]);
    setUnreadCount(0);
    setIsOpen(false);
  };

  const getIcon = (type) => {
    switch(type) {
      case 'critical': return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case 'warning': return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'success': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'report': return <FileText className="w-5 h-5 text-blue-500" />;
      default: return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  const formatTime = (date) => {
    if (!date) return '';
    const diff = Date.now() - date.getTime();
    const minutes = Math.floor(diff / 60000);

    if (minutes < 1) return t('notifications.justNow');
    if (minutes < 60) return t('notifications.minutesAgo', { count: minutes });
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return t('notifications.hoursAgo', { count: hours });
    return t('notifications.daysAgo', { count: Math.floor(hours / 24) });
  };

  const handleNotificationClick = (notif) => {
    markAsRead(notif.id);

    // Si c'est un rapport, proposer le telechargement
    if (notif.reportFilename) {
      const downloadUrl = `/api/v1/reports/download/${notif.reportFilename}`;
      window.open(downloadUrl, '_blank');
    } else if (notif.scanId) {
      // Naviguer vers le scan
      navigate(`/scans/${notif.scanId}`);
      setIsOpen(false);
    }
  };

  // Dropdown content rendered via Portal
  const dropdownContent = isOpen && createPortal(
    <>
      {/* Overlay to close on click outside */}
      <div
        className="fixed inset-0"
        style={{ zIndex: 99998 }}
        onClick={() => setIsOpen(false)}
      />

      {/* Dropdown - Responsive width */}
      <div
        className="fixed w-[90vw] sm:w-80 md:w-96 max-w-[384px] bg-brand-black-light border-2 border-brand-green/30 rounded-xl shadow-2xl"
        style={{
          zIndex: 99999,
          top: dropdownPosition.top,
          right: dropdownPosition.right
        }}
      >
        {/* Header */}
        <div className="p-4 border-b border-brand-green/20 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-white">{t('nav.notifications')}</h3>
            <p className="text-xs text-gray-400">{t('notifications.unread', { count: unreadCount })}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={markAllAsRead}
              className="text-xs text-brand-green hover:underline"
            >
              {t('notifications.markAllRead')}
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 hover:bg-brand-black rounded"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* List */}
        <div className="max-h-96 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Bell className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>{t('notifications.none')}</p>
            </div>
          ) : (
            notifications.map((notif) => (
              <div
                key={notif.id}
                onClick={() => handleNotificationClick(notif)}
                className={`p-4 border-b border-gray-800 cursor-pointer transition-colors ${
                  notif.read ? 'bg-brand-black-light' : 'bg-brand-black hover:bg-brand-black-lighter'
                }`}
              >
                <div className="flex items-start gap-3">
                  {getIcon(notif.type)}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <div className={`font-medium ${notif.read ? 'text-gray-400' : 'text-white'}`}>
                        {notif.title}
                      </div>
                      {!notif.read && (
                        <div className="w-2 h-2 rounded-full bg-brand-green"></div>
                      )}
                    </div>
                    <p className="text-sm text-gray-400 mb-2">{notif.message}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <Clock className="w-3 h-3" />
                        {formatTime(notif.timestamp)}
                      </div>
                      {notif.reportFilename && (
                        <div className="flex items-center gap-1 text-xs text-brand-green">
                          <Download className="w-3 h-3" />
                          <span>{t('notifications.download')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      dismissNotification(notif.id);
                    }}
                    className="p-1 hover:bg-brand-black-lighter rounded opacity-50 hover:opacity-100"
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="p-3 border-t border-brand-green/20 flex justify-between items-center">
            <button
              onClick={clearAll}
              className="text-xs text-red-400 hover:underline"
            >
              {t('notifications.clearAll')}
            </button>
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/notifications');
              }}
              className="text-xs text-brand-green hover:underline flex items-center gap-1"
            >
              <span>{t('notifications.viewAll')}</span>
              <ExternalLink className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>
    </>,
    document.body
  );

  return (
    <div className="relative">
      {/* Bell Icon */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 hover:bg-brand-black-lighter rounded-lg transition-colors"
      >
        <Bell className="w-6 h-6 text-gray-400 hover:text-brand-green" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {dropdownContent}
    </div>
  );
};

export default NotificationCenter;
